/**
 * SSERequest - Custom request handler for Server-Sent Events
 * Extends AbstractXRequestClass to integrate with @ant-design/x-sdk
 *
 * @module services/chat/sse-provider/sse-request
 */

import type {
  FollowUpQueriesPayload,
  MessageChunkPayload,
  ParsedSSEEvent,
  ProcessingStepPayload,
  SSEEvent,
} from "~/common/types";

import { AbstractXRequestClass } from "@ant-design/x-sdk";

import {
  SSEChatInput,
  SSEChatOutput,
  SSERequestConfig,
  SSEXRequestOptions,
} from "./_types";

import { SSE_EVENT_TYPE } from "~/common/constants";
import { SSEEventHelper } from "~/common/helpers";

/**
 * SSERequest class for handling EventSource connections
 *
 * @remarks
 * This class manages the lifecycle of SSE connections including:
 * - Establishing EventSource connection
 * - Parsing and routing SSE events
 * - Accumulating message chunks
 * - Error handling and reconnection
 * - Aborting active streams
 *
 * @example
 * ```typescript
 * const request = new SSERequest('https://api.example.com', {
 *   callbacks: {
 *     onUpdate: (chunk) => console.log(chunk),
 *     onSuccess: (data) => console.log('Done', data),
 *     onError: (error) => console.error(error),
 *   },
 *   config: { token: 'Bearer abc123' },
 * });
 *
 * request.run({
 *   message: { content: 'Hello', role: 'user' },
 *   external_user_id: 'user-123',
 * });
 * ```
 */
export class SSERequest<
  Input extends SSEChatInput = SSEChatInput,
  Output extends SSEChatOutput = SSEChatOutput,
> extends AbstractXRequestClass<Input, Output> {
  private abortController: AbortController | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private _isRequesting = false;
  private _isTimeout = false;
  private _isStreamTimeout = false;
  private accumulatedChunks: Output[] = [];
  private config: SSERequestConfig;

  /**
   * Creates an instance of SSERequest
   *
   * @param baseURL - Base API URL
   * @param options - Request options including callbacks and config
   */
  constructor(options: SSEXRequestOptions<Input, Output>) {
    super(options.config.baseURL, options);
    this.config = options.config;
  }

  /**
   * Async handler (required by AbstractXRequestClass)
   */
  get asyncHandler(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Check if request has timed out
   */
  get isTimeout(): boolean {
    return this._isTimeout;
  }

  /**
   * Check if stream has timed out
   */
  get isStreamTimeout(): boolean {
    return this._isStreamTimeout;
  }

  /**
   * Check if request is currently active
   */
  get isRequesting(): boolean {
    return this._isRequesting;
  }

  /**
   * Manual mode flag (required by AbstractXRequestClass)
   */
  get manual(): boolean {
    return true;
  }

  /**
   * Builds the SSE endpoint URL
   *
   * @returns Clean endpoint URL
   */
  private buildURL(): string {
    return `${this.baseURL}/chat`;
  }

  /**
   * Builds the request body payload
   *
   * @param params - Request parameters
   * @returns Request body object
   *
   * @remarks
   * Body format: { session_id?: string, message: { content: string } }
   */
  private buildRequestBody(params: Input): object {
    const { external_user_id, session_id, message } = params;

    const payload: Record<string, any> = {
      message: {
        content:
          typeof message?.content === "string"
            ? message.content
            : message?.content?.text || "",
      },
    };

    // Add optional session_id
    if (session_id) {
      payload.session_id = session_id;
    }

    // Add external_user for tracking
    if (external_user_id) {
      payload.external_user = external_user_id;
    }

    return payload;
  }

  /**
   * Logs debug information if debug mode is enabled
   */
  private log(message: string, ...args: unknown[]): void {
    if (this.config.debug) {
      console.log(`[SSERequest] ${message}`, ...args);
    }
  }

  /**
   * Routes parsed events to appropriate handlers
   *
   * @param event - Parsed SSE event
   *
   * @remarks
   * Backend SSE events:
   * 1. chat_session (optional, only for new sessions)
   *    data: {"id":"session-id","name":"Session Name"}
   * 2. step (multiple, processing steps)
   *    data: {"step":"Thinking..."}
   * 3. message (multiple chunks, indexed)
   *    data: {"content":"text","index":0}
   * 4. end (stream complete)
   */
  private routeEvent(event: ParsedSSEEvent): void {
    switch (event.event) {
      case SSE_EVENT_TYPE.CHAT_SESSION:
        this.handleChatSessionEvent(event.payload);
        break;

      case SSE_EVENT_TYPE.STEP:
        this.handleStepEvent(event.payload);
        break;

      case SSE_EVENT_TYPE.MESSAGE:
        this.handleMessageEvent(event.payload);
        break;

      case SSE_EVENT_TYPE.FOLLOW_UP_QUERIES:
        this.handleFollowUpQueriesEvent(event.payload);
        break;

      case SSE_EVENT_TYPE.END:
        this.handleEndEvent();
        break;
    }
  }

  /**
   * Handles chat session creation event
   *
   * @param payload - Chat session payload with id and name
   *
   * @remarks
   * This event is only sent when a new session is created (no session_id in request).
   * Format: {"id":"3fa85f64-...","name":"Mock Name"}
   */
  private handleChatSessionEvent(payload: any): void {
    this.log("Chat session created:", payload);

    const { callbacks } = this.options;

    // Create step chunk with metadata to identify as chat_session
    const stepChunk = {
      session_id: payload.id || "",
      metadata: {
        type: "chat_session",
        timestamp: new Date().toISOString(),
      },
    } as unknown as Output;

    this.accumulatedChunks.push(stepChunk);

    // Notify update callback for step events
    callbacks?.onUpdate?.(stepChunk, new Headers());
  }

  /**
   * Handles processing step events
   *
   * @param payload - Step event payload
   *
   * @remarks
   * Step events are sent during model thinking/processing phases.
   * These are accumulated as separate chunks to display in Think component.
   */
  private handleStepEvent(payload: ProcessingStepPayload): void {
    this.log("Processing step:", payload.step);

    const { callbacks } = this.options;

    // Create step chunk with metadata to identify as step
    const stepChunk = {
      text: payload.step || "",
      metadata: {
        type: "step",
        timestamp: new Date().toISOString(),
      },
    } as unknown as Output;

    this.accumulatedChunks.push(stepChunk);

    // Notify update callback for step events
    callbacks?.onUpdate?.(stepChunk, new Headers());
  }

  /**
   * Handles message chunk events
   *
   * @param payload - Message chunk payload
   *
   * @remarks
   * Backend sends message chunks in format:
   * event: message
   * data: {"content":"text chunk","index":0}
   *
   * Each chunk is accumulated to build the complete message.
   */
  private handleMessageEvent(payload: MessageChunkPayload): void {
    const { callbacks } = this.options;

    // Create output chunk from content field with type metadata
    const chunk = {
      text: payload.content || "",
      metadata: {
        type: "message",
        ...(payload.metadata || {}),
      },
    } as unknown as Output;

    this.accumulatedChunks.push(chunk);

    this.log(`Message chunk:`, payload.content);

    // Notify update callback
    callbacks?.onUpdate?.(chunk, new Headers());
  }

  /**
   * Handles follow-up queries event
   *
   * @param payload - Follow-up queries payload
   */
  private handleFollowUpQueriesEvent(payload: FollowUpQueriesPayload): void {
    const { callbacks } = this.options;

    const chunk = {
      follow_up_queries: payload.queries || [],
      metadata: {
        type: "follow_up_queries",
        timestamp: new Date().toISOString(),
      },
    } as unknown as Output;

    this.accumulatedChunks.push(chunk);
    callbacks?.onUpdate?.(chunk, new Headers());
  }

  /**
   * Handles end of stream event
   */
  private handleEndEvent(): void {
    const { callbacks } = this.options;

    this.log("Stream ended");

    this._isRequesting = false;

    // Notify success callback with all accumulated chunks
    callbacks?.onSuccess?.(this.accumulatedChunks, new Headers());

    this.cleanup();
  }

  /**
   * Handles errors during streaming
   *
   * @param error - Error object
   */
  private handleError(error: unknown): void {
    const { callbacks } = this.options;

    this._isRequesting = false;

    const sseError = SSEEventHelper.createError(error);

    // Don't trigger error callback for abort errors
    if (SSEEventHelper.isAbortError(error)) {
      this.log("Stream aborted");
      callbacks?.onError?.(new DOMException("Stream aborted", "AbortError"));
      // Abort is handled silently
    } else {
      console.error("[SSERequest] Stream error:", sseError);
      callbacks?.onError?.(new Error(sseError.message));
    }

    this.cleanup();
  }

  /**
   * Parses and handles a single SSE event string
   *
   * @param eventString - Raw event string from stream
   *
   * @remarks
   * SSE format:
   * event: message
   * data: {"content":"text"}
   * id: 123
   *
   * (blank line separator)
   *
   * Handles both Unix (\n) and Windows (\r\n) line endings
   */
  private parseAndHandleEvent(eventString: string): void {
    // Split by \n or \r\n to handle both Unix and Windows line endings
    const lines = eventString.split(/\r?\n/);
    let eventType = "message"; // default
    let data = "";
    let id = "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        // Multiple data lines should be concatenated with newline
        if (data) {
          data += "\n";
        }
        data += line.slice(5).trim();
      } else if (line.startsWith("id:")) {
        id = line.slice(3).trim();
      }
      // Ignore other fields (retry:, comment lines starting with :)
    }

    if (data) {
      // Create SSE event and handle
      const sseEvent: SSEEvent = {
        event: eventType as any,
        data,
        id,
      };

      this.log("Parsed SSE event:", sseEvent);

      const parsed = SSEEventHelper.parseSSEEvent(sseEvent);
      if (parsed) {
        this.routeEvent(parsed);
      }
    }
  }

  /**
   * Reads and processes the SSE stream
   *
   * @param reader - ReadableStream reader
   */
  private async readStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          this.log("Stream complete");
          break;
        }

        // Decode chunk
        buffer += decoder.decode(value, { stream: true });

        // Split by double newline (SSE event separator)
        // Handle both Unix (\n\n) and Windows (\r\n\r\n) line endings
        const events = buffer.split(/\r?\n\r?\n/);

        // Process all complete events
        for (let i = 0; i < events.length - 1; i++) {
          if (events[i].trim()) {
            this.parseAndHandleEvent(events[i]);
          }
        }

        // Keep incomplete event in buffer
        buffer = events[events.length - 1];
      }
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        this.log("Stream aborted");
      } else {
        throw error;
      }
    }
  }

  /**
   * Cleans up fetch stream connection
   */
  private cleanup(): void {
    if (this.reader) {
      this.reader.cancel().catch(() => {
        // Ignore cancel errors
      });
      this.reader = null;
    }

    this.abortController = null;
    this.log("Stream cleaned up");
  }

  /**
   * Runs the SSE request using Fetch API
   *
   * @param params - Request parameters including message and user info
   *
   * @remarks
   * Uses fetch() with POST method to properly send JSON body and authorization header.
   * Reads response as SSE stream and manually parses events.
   */
  run(params?: Input): void {
    if (!params) {
      console.error("[SSERequest] No parameters provided");
      return;
    }

    this.log("Starting SSE request", params);

    // Reset state
    this._isRequesting = true;
    this._isTimeout = false;
    this._isStreamTimeout = false;
    this.accumulatedChunks = [];

    // Create abort controller
    this.abortController = new AbortController();

    // Build request
    const url = this.buildURL();
    const body = this.buildRequestBody(params);

    this.log("Request URL:", url);
    this.log("Request body:", body);

    // Start fetch
    this.fetchSSE(url, body).catch((error) => {
      this.handleError(error);
    });
  }

  /**
   * Performs the fetch request and reads the SSE stream
   */
  private async fetchSSE(url: string, body: object): Promise<void> {
    // Fetch with POST
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.config.token}`,
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal: this.abortController!.signal,
    });

    this.log("Response status:", response.status);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    // Read stream
    this.reader = response.body.getReader();
    await this.readStream(this.reader);
  }

  /**
   * Aborts the active SSE stream
   */
  abort(): void {
    this.log("Aborting SSE request");

    if (this.abortController) {
      this.abortController.abort();
      this._isRequesting = false;

      this.options.callbacks?.onError?.(
        new DOMException("Stream aborted by user", "AbortError"),
      );
    }

    this.cleanup();
  }
}
